'use client';

import { useCallback, useEffect, useRef } from 'react';
import { useReactiveRef } from './useReactiveRef';
import { useDispatch, useSelector } from 'react-redux';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import logger from '@/lib/logger';
import { getOrCreateCallId } from '@/lib/calls/getOrCreateCallId';
import {
  setIncidentNumber,
  setPhonePin,
  setPhoneNumber,
  hydrateCallInfoState,
} from '@/store/slices/callInfoSlice';
import { RootState } from '@/store/store';
import { mergeCallInfoFromDb } from '@/lib/calls/callInfoMerge';

/**
 * Call Info Real-Time Sync Hook
 *
 * Provides real-time Supabase sync for call info (case number, phone number, phone pin).
 * - Debounced writes (1s) to prevent rate limiting during typing
 * - Hydrates from Supabase on mount
 * - Supabase is the only durable source of truth for these fields
 */

const SYNC_DEBOUNCE_MS = 1000;

interface UseCallInfoSyncOptions {
  /** Call ID (room name) for scoping */
  callId: string;
  /** Whether sync is enabled (default: true) */
  enabled?: boolean;
  /** Custom debounce time in ms */
  debounceMs?: number;
}

interface CallInfoSyncState {
  isLoading: boolean;
  isSyncing: boolean;
  lastSyncTime: number | null;
  error: Error | null;
  dbCallId: string | null;
}

/**
 * Hash the durable call-info fields. Used by BOTH the debounced writer (to skip
 * redundant syncs) AND the reconnect dirty-guard (#725) — they MUST hash the
 * exact same shape, so this is the single source of that shape.
 */
function hashCallInfo(c: {
  incidentNumber: string | null;
  phoneNumber: string | null;
  phonePin: string | null;
}): string {
  return JSON.stringify({ i: c.incidentNumber, pn: c.phoneNumber, pp: c.phonePin });
}

/**
 * Hook to sync call info to Supabase in real-time
 */
export function useCallInfoSync(
  options: UseCallInfoSyncOptions,
): CallInfoSyncState & { reloadFromSupabase: () => Promise<void> } {
  const { callId, enabled = true, debounceMs = SYNC_DEBOUNCE_MS } = options;

  const dispatch = useDispatch();
  const { user, isLoading: authLoading } = useAuth();
  const supabase = createClient();

  // Select call info from Redux
  const callInfo = useSelector((state: RootState) => state.callInfo);
  const { incidentNumber, phoneNumber, phonePin } = callInfo;

  // Live mirror of current Redux call-info so the reconnect dirty-guard (#725)
  // can read it without widening loadFromSupabase's dependency array.
  const callInfoRef = useRef(callInfo);
  callInfoRef.current = callInfo;

  // Refs for tracking state without triggering re-renders
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSyncedHashRef = useRef<string | null>(null);
  // #725-followup/F1: per-field baseline (what we believe is persisted in the DB),
  // so the reconnect re-sync can tell WHICH field the operator edited locally and
  // merge per-field instead of all-or-nothing. Kept in lock-step with
  // lastSyncedHashRef (every site that sets one sets the other from the same values).
  const lastSyncedFieldsRef = useRef<{
    incidentNumber: string | null;
    phoneNumber: string | null;
    phonePin: string | null;
  } | null>(null);
  // #945: bumped after every SUCCESSFUL write. `loadFromSupabase` captures it
  // before its SELECT so a response that was overtaken by a write can be
  // recognised as stale — otherwise the late row (still missing the value the
  // writer just persisted) is applied as authoritative and wipes it again.
  const writeGenerationRef = useRef(0);
  const dbCallIdRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const hasLoadedRef = useRef(false);
  const stateRef = useReactiveRef<CallInfoSyncState>({
    isLoading: true,
    isSyncing: false,
    lastSyncTime: null,
    error: null,
    dbCallId: null,
  });

  /**
   * Get or create the DB call ID (UUID) for this call
   */
  const getOrCreateDbCallId = useCallback(async (): Promise<string | null> => {
    if (dbCallIdRef.current) {
      return dbCallIdRef.current;
    }

    try {
      const dbCallId = await getOrCreateCallId(supabase, callId, {
        userId: user?.id ?? null,
      });
      if (dbCallId) {
        dbCallIdRef.current = dbCallId;
      }
      return dbCallId;
    } catch (err: unknown) {
      logger.error('useCallInfoSync', 'Failed to get/create call ID', { error: err, callId });
      return null;
    }
  }, [callId, supabase, user?.id]);

  /**
   * Sync call info to Supabase
   */
  const syncToSupabase = useCallback(async (data: {
    incidentNumber: string | null;
    phoneNumber: string | null;
    phonePin: string | null;
  }) => {
    if (!enabled || !callId) return;

    // Compute hash to avoid redundant syncs
    const hash = hashCallInfo({
      incidentNumber: data.incidentNumber,
      phoneNumber: data.phoneNumber,
      phonePin: data.phonePin,
    });

    if (hash === lastSyncedHashRef.current) {
      logger.debug('useCallInfoSync', 'Skipping sync - no changes');
      return;
    }

    stateRef.current = { ...stateRef.current, isSyncing: true };

    try {
      const dbCallId = await getOrCreateDbCallId();
      if (!dbCallId) {
        throw new Error('Failed to get/create call ID');
      }

      // Update calls table with the DB-backed call info fields.
      const updatePayload: Record<string, string | null> = {
        case_number: data.incidentNumber,
        phone_number: data.phoneNumber,
        phone_pin: data.phonePin,
      };
      const { error } = await supabase
        .from('calls')
        .update(updatePayload)
        .eq('id', dbCallId);

      if (error) {
        throw error;
      }

      lastSyncedHashRef.current = hash;
      lastSyncedFieldsRef.current = {
        incidentNumber: data.incidentNumber,
        phoneNumber: data.phoneNumber,
        phonePin: data.phonePin,
      };
      writeGenerationRef.current += 1;
      stateRef.current = {
        ...stateRef.current,
        isSyncing: false,
        lastSyncTime: Date.now(),
        error: null,
        dbCallId,
      };

      logger.debug('useCallInfoSync', 'Synced call info to Supabase', {
        callId,
        incidentNumber: data.incidentNumber,
        hasPhoneNumber: Boolean(data.phoneNumber),
        hasPhonePin: Boolean(data.phonePin),
      });
    } catch (err: unknown) {
      logger.error('useCallInfoSync', 'Failed to sync to Supabase', { error: err });
      stateRef.current = {
        ...stateRef.current,
        isSyncing: false,
        error: err instanceof Error ? err : new Error(String(err)),
      };
    }
  }, [enabled, callId, getOrCreateDbCallId, supabase]);

  // #725-followup/F1: live mirror of the latest syncToSupabase so the reconnect
  // merge below can trigger an immediate (deduped) flush of a kept dirty field
  // WITHOUT widening loadFromSupabase's dependency array (same pattern as callInfoRef).
  const syncToSupabaseRef = useRef(syncToSupabase);
  syncToSupabaseRef.current = syncToSupabase;

  /**
   * Load call info from Supabase (on mount)
   */
  const loadFromSupabase = useCallback(async (opts?: { force?: boolean }) => {
    if (!enabled || !callId || (!opts?.force && hasLoadedRef.current)) {
      stateRef.current = { ...stateRef.current, isLoading: false };
      return;
    }

    // #725-followup/F1: the old all-or-nothing dirty-guard skipped the WHOLE reload
    // when ANY field was locally dirty, then the debounced writer re-wrote the
    // untouched-but-stale companions and clobbered a peer's concurrent clear. We now
    // always fetch and do a per-field merge below (operator's dirty field wins; the
    // DB value is taken for every field the operator did NOT touch).
    hasLoadedRef.current = true;

    // #945: snapshot the write generation BEFORE the SELECT leaves. If a write
    // succeeds while this read is in flight, the row we are about to receive
    // predates it and must not be applied — the merge would see a baseline that
    // already matches Redux, call every field clean, take the stale (empty)
    // remote value and never flush it back.
    const readGeneration = writeGenerationRef.current;

    try {
      const { data: callData, error: callError } = await supabase
        .from('calls')
        .select('id, case_number, phone_number, phone_pin, caller_name')
        .eq('call_id', callId)
        .maybeSingle();

      if (callError && callError.code !== 'PGRST116') {
        throw callError;
      }

      if (!callData?.id) {
        stateRef.current = { ...stateRef.current, isLoading: false };
        return;
      }

      dbCallIdRef.current = callData.id;

      if (writeGenerationRef.current !== readGeneration) {
        // A write landed while we were reading. Its values are already in both
        // Redux and the DB, and lastSyncedFieldsRef/lastSyncedHashRef were set
        // from what it wrote, so there is nothing to merge — drop the stale row.
        logger.info('useCallInfoSync', 'Discarded a load overtaken by a write', {
          callId,
        });
        stateRef.current = {
          ...stateRef.current,
          isLoading: false,
          dbCallId: callData.id,
        };
        return;
      }

      if (mountedRef.current) {
        // #725-followup A (Codex HIGH): the DB row is the durable source of truth.
        // incident stays '' (string) to match its consumers + the original `|| ''`
        // behaviour; phone/pin use the raw DB value (null when cleared), matching the
        // slice's initial representation.
        const dbIncidentNumber = callData.case_number ?? '';
        const dbPhoneNumber = callData.phone_number ?? null;
        const dbPhonePin = callData.phone_pin ?? null;

        // Per-field dirty-merge (#725-followup/F1, #945). The rules and the
        // reasoning live in lib/calls/callInfoMerge.ts, which is unit-tested —
        // this is the logic that silently dropped the pre-join case number, so
        // it does not belong inline here.
        const baseline = lastSyncedFieldsRef.current;
        const cur = callInfoRef.current;
        const { merged, dirty, hasUnflushed } = mergeCallInfoFromDb({
          baseline,
          local: {
            incidentNumber: cur.incidentNumber,
            phoneNumber: cur.phoneNumber,
            phonePin: cur.phonePin,
          },
          remote: {
            incidentNumber: dbIncidentNumber,
            phoneNumber: dbPhoneNumber,
            phonePin: dbPhonePin,
          },
          // The ONLY declared not-yet-persisted handoff: the case number the
          // officer entered in pre-join, which CustomPreJoin also stores in
          // preJoinData. Matching against it is what distinguishes "my pre-join
          // value has not reached the row yet" from "Redux still holds a stale
          // value from an earlier mount" — call-info is never cleared between
          // mounts, so truthiness alone could resurrect a peer's deliberate
          // clear. Phone/PIN are persisted by the connection-details route
          // before they reach Redux, so they get no exception.
          pendingHandoff: { incidentNumber: cur.preJoinData?.caseNumber ?? null },
        });
        const {
          incidentNumber: nextIncidentNumber,
          phoneNumber: nextPhoneNumber,
          phonePin: nextPhonePin,
        } = merged;

        dispatch(hydrateCallInfoState({
          incidentNumber: nextIncidentNumber,
          phoneNumber: nextPhoneNumber,
          phonePin: nextPhonePin,
        }));

        // Baseline := the DB row we just read (NOT the merged state). Any dirty field
        // we kept locally is not yet persisted, so a DB-valued baseline lets the writer
        // detect the diff and flush it, while the clean fields already match the DB →
        // no clobber. lastSyncedFieldsRef + lastSyncedHashRef stay in lock-step.
        lastSyncedFieldsRef.current = {
          incidentNumber: dbIncidentNumber,
          phoneNumber: dbPhoneNumber,
          phonePin: dbPhonePin,
        };
        lastSyncedHashRef.current = hashCallInfo(lastSyncedFieldsRef.current);

        // Guarantee a kept dirty field is flushed. The debounce effect only re-fires
        // if the merge dispatch actually CHANGED Redux — but when no peer touched a
        // clean field the merged state equals current Redux, so the dispatch is a no-op
        // and the effect does NOT re-run; the explicit flush is then the PRIMARY way the
        // edit reaches the DB (it also covers a pending debounce that already fired-and-
        // failed during a full-network blip). syncToSupabase no-ops via its own hash
        // check if nothing needs writing, so a concurrent re-fired debounce cannot
        // double-write.
        if (hasUnflushed) {
          void syncToSupabaseRef.current({
            incidentNumber: nextIncidentNumber,
            phoneNumber: nextPhoneNumber,
            phonePin: nextPhonePin,
          });
        }

        logger.info('useCallInfoSync', 'Loaded from Supabase', {
          incidentNumber: callData.case_number,
          hasPhoneNumber: Boolean(callData.phone_number),
          hasPhonePin: Boolean(callData.phone_pin),
          callerName: callData.caller_name,
          merged: baseline !== null,
          keptDirty: dirty,
        });
      }

      stateRef.current = {
        ...stateRef.current,
        isLoading: false,
        dbCallId: callData.id,
      };
    } catch (err: unknown) {
      logger.error('useCallInfoSync', 'Failed to load from Supabase', { error: err });
      stateRef.current = {
        ...stateRef.current,
        isLoading: false,
        error: err instanceof Error ? err : new Error(String(err)),
      };
    }
  }, [enabled, callId, supabase, dispatch]);

  // #725: force a re-sync from Supabase on LiveKit reconnect (dirty-guard above
  // protects un-flushed local edits). isReloadingRef collapses back-to-back
  // reconnects into a single in-flight fetch.
  const isReloadingRef = useRef(false);
  const reloadFromSupabase = useCallback(async () => {
    if (isReloadingRef.current) return;
    isReloadingRef.current = true;
    try {
      await loadFromSupabase({ force: true });
    } finally {
      isReloadingRef.current = false;
    }
  }, [loadFromSupabase]);

  // Load from Supabase on mount
  useEffect(() => {
    if (!enabled || authLoading) return;

    loadFromSupabase();
  }, [enabled, authLoading, loadFromSupabase]);

  // Debounced sync when call info changes
  useEffect(() => {
    if (!enabled || !callId) return;

    // Cancel pending sync
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    // Schedule new sync
    syncTimeoutRef.current = setTimeout(() => {
      syncToSupabase({
        incidentNumber,
        phoneNumber,
        phonePin,
      });
    }, debounceMs);
  }, [enabled, callId, incidentNumber, phoneNumber, phonePin, debounceMs, syncToSupabase]);

  // Cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  return { ...stateRef.current, reloadFromSupabase };
}

export default useCallInfoSync;
